import React, { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Box, Grid, MenuItem, TextField } from "@mui/material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import {
  getAccountsApplications,
  getAllAccounts,
} from "../../services/testService";
import type { AccountListItem } from "../../types/notificationTest";

interface AccountAppPickerProps {
  onAccountAppChange?: (accountId: string, appId: string) => void;
}

export const AccountAppPicker: React.FC<AccountAppPickerProps> = ({
  onAccountAppChange,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { control, setValue, watch } = useFormContext();
  const accountId = watch("accountId") as string;

  const [accounts, setAccounts] = useState<AccountListItem[]>([]);
  const [apps, setApps] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    getAllAccounts()
      .then((res) => {
        if (!cancelled) setAccounts(res);
      })
      .catch((err) => {
        notify(
          extractErrorMessage(
            err,
            "resources.test.notifications.loadAccountsFailed",
            translate,
          ),
          { type: "error" },
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!accountId) {
      setApps([]);
      return;
    }
    let cancelled = false;
    getAccountsApplications(accountId)
      .then((res) => {
        if (!cancelled) setApps(res);
      })
      .catch(() => {
        if (!cancelled) setApps([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return (
    <Box>
      <Grid container spacing={{ xs: 1.5, sm: 3 }}>
        <Grid item xs={12} md={6}>
          <Controller
            name="accountId"
            control={control}
            rules={{
              required: translate("resources.test.validation.accountRequired"),
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                required
                label={translate("resources.test.fields.account")}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                onChange={(e) => {
                  field.onChange(e);
                  setValue("appId", "", { shouldDirty: true });
                  setValue("message.templateId", "", { shouldDirty: true });
                  onAccountAppChange?.(String(e.target.value), "");
                }}
              >
                {accounts.map((a) => (
                  <MenuItem key={a.id} value={a.id}>
                    {a.accountName}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Controller
            name="appId"
            control={control}
            rules={{
              required: translate(
                "resources.test.validation.applicationRequired",
              ),
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                select
                fullWidth
                required
                disabled={!accountId}
                label={translate("resources.test.fields.application")}
                InputLabelProps={{ shrink: true }}
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                sx={{
                  "& .MuiOutlinedInput-root.Mui-disabled.Mui-error .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "error.main",
                    },
                }}
                onChange={(e) => {
                  field.onChange(e);
                  setValue("message.templateId", "", { shouldDirty: true });
                  onAccountAppChange?.(accountId, String(e.target.value));
                }}
              >
                {apps.map((app) => (
                  <MenuItem key={app} value={app}>
                    {app}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AccountAppPicker;
